import z from 'zod';

import type { List } from '@/utils/list';

/**
 * People API `subscription` tag — see claude-context/subscriptions-api.md.
 *
 * A subscription belongs to a *service* (vessel), not a person: one service has at
 * most one subscription row, whatever its state. The service is resolved from the
 * `X-Service-Id` header the api client attaches.
 */

// `harbor` is the free default and has no Stripe object behind it. It never appears
// in a request enum — only navigate/command are purchasable.
export enum SubscriptionTier {
  HARBOR = 'harbor',
  NAVIGATE = 'navigate',
  COMMAND = 'command',
}

export type PaidSubscriptionTier = SubscriptionTier.NAVIGATE | SubscriptionTier.COMMAND;

export const PAID_TIERS = [SubscriptionTier.NAVIGATE, SubscriptionTier.COMMAND] as const;

export enum BillingInterval {
  MONTHLY = 'monthly',
  ANNUALLY = 'annually',
}

/**
 * Not declared as an enum by the spec — `payment_status` comes back as a bare string
 * and only `incomplete` is documented. These are the Stripe values the backend mirrors;
 * anything unrecognised maps to `null` and is preserved in `paymentStatusRaw`.
 */
export enum PaymentStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
  ENDED = 'ended',
}

export enum CancelFeedback {
  TOO_EXPENSIVE = 'too_expensive',
  UNUSED = 'unused',
  SWITCHED_SERVICE = 'switched_service',
  CUSTOMER_SERVICE = 'customer_service',
  LOW_QUALITY = 'low_quality',
  MISSING_FEATURES = 'missing_features',
  OTHER = 'other',
}

// ---------------------------------------------------------------------------
// DTOs (wire shapes)
// ---------------------------------------------------------------------------

export interface SubscriptionDto {
  id: string;
  service_id: string;
  tier: string;
  interval: string;
  seats: number;
  crew_seats: number;
  payment_status: string;
  next_payment_at: string;
  cancel_at_period_end: boolean;
  cancel_at: string;
  canceled_at: string;
  /**
   * The answer the vessel's payer gave to "are you going to add an owner?" — asked only of a
   * captain paying for a vessel that has none. `true` holds one management seat empty for the
   * owner still to come; `false` — the column's default, and what every plan an owner bought
   * carries — hands that seat to the captain.
   */
  captain_will_add_owner: boolean;
  /**
   * Whether the caller may move this plan onto their own card: somebody else pays for it and the
   * caller runs the vessel (owner, co-owner, or a captain on a vessel with no owner). Answered by
   * `GET /subscriptions`; rows from `/subscriptions/all` leave it out.
   */
  can_take_over?: boolean;
  /**
   * Granted by a Vessel Haven admin rather than bought: never billed, never expiring, and
   * closed to every change from the vessel's side — the API refuses a plan change, checkout,
   * cancel or resume with 409 `SUBSCRIPTION_MANUALLY_GRANTED`.
   */
  manually_granted?: boolean;
}

// `unread` is a generic page-wrapper field reused from notifications — meaningless here.
export type SubscriptionsPageDto = List<SubscriptionDto> & { unread?: number };

export interface CreateCheckoutSubscriptionDto {
  tier: PaidSubscriptionTier;
  interval: BillingInterval;
  seats: number;
  crew_seats?: number;
  /** See `UpdateSubscriptionDto.will_add_owner`. Stored on the row this checkout creates. */
  will_add_owner?: boolean;
}

export interface UpdateSubscriptionDto {
  tier?: PaidSubscriptionTier;
  interval?: BillingInterval;
  seats?: number;
  crew_seats?: number;
  /**
   * Unlike every other field here, an omitted `will_add_owner` does **not** keep its current
   * value. `ChangeSubscription` writes `req.WillAddOwner` unconditionally (the People
   * service's `internal/app/payments/service.go`) and the field is a plain Go `bool`, so a
   * body without it decodes to `false` and clears the answer — moving the reserved management
   * seat off the owner-to-be and onto the captain as a side effect of, say, buying a crew
   * seat. So it is always sent for a vessel the question applies to, never left out.
   */
  will_add_owner?: boolean;
}

export interface CancelSubscriptionDto {
  feedback?: CancelFeedback;
  comment?: string;
}

export interface StripeUrlDto {
  url: string;
}

/**
 * Referral is not a subscription resource of its own: the people service exposes the
 * whole system as three read-only fields on the person record (`dto.PersonResponse`,
 * served by `GET /me`). There is no endpoint to issue, redeem or list referrals — the
 * link is minted server-side and `referred_by` is fixed at sign-up.
 *
 * Only the fields this store reads are declared; `/me` returns the full person.
 */
export interface ReferralDto {
  referral_link: string;
  referrals_count: number;
  referred_by: string;
}

/**
 * The spec types `amount` as `currency.Amount`, declared as an empty object with no
 * properties. This is the shape the endpoint actually returns (confirmed by the
 * pre-existing pricing page, which reads `amount.number`).
 */
export interface PriceDto {
  id: string;
  lookup_key: string;
  amount: {
    number: string;
    currency: string;
  };
}

// ---------------------------------------------------------------------------
// Domain models
// ---------------------------------------------------------------------------

export interface Subscription {
  id: string;
  serviceId: string;
  /** `null` when the backend returns a tier this client doesn't know. */
  tier: SubscriptionTier | null;
  interval: BillingInterval | null;
  seats: number;
  /** Add-on count, separate from the billable `seats`. */
  crewSeats: number;
  paymentStatus: PaymentStatus | null;
  /** Unmapped `payment_status`, kept so unknown states stay debuggable. */
  paymentStatusRaw: string;
  /** Next renewal charge. `null` when unset (harbor, or an ended subscription). */
  nextPaymentAt: string | null;
  cancelAtPeriodEnd: boolean;
  /** When access actually ends. */
  cancelAt: string | null;
  /** When the cancel request was made. */
  canceledAt: string | null;
  /**
   * Whether the payer said an owner is still coming, which holds one management seat empty for
   * them. Only meaningful while the vessel has no owner — once one joins, the reserved seat is
   * theirs whatever the answer was.
   */
  captainWillAddOwner: boolean;
  /** The viewer may take this plan over onto their own card; `false` when the backend did not say. */
  canTakeOver: boolean;
  /** A lifetime plan an admin granted — see `SubscriptionDto.manually_granted`. */
  manuallyGranted: boolean;
}

export type SubscriptionsList = List<Subscription>;

export interface Price {
  id: string;
  lookupKey: string;
  amount: number;
  currency: string;
}

/**
 * The caller's own referral standing. Belongs to the person, not to a service — it does
 * not change when the active vessel does.
 *
 * The API rewards nothing on its own: it exposes the link and the head count, and any
 * "free month per invite" tiering is a product rule the UI has to define itself.
 */
export interface Referral {
  /** Shareable invite URL. `null` when the backend has not issued one. */
  link: string | null;
  /** How many people signed up through this link. */
  count: number;
  /** Id of the person who referred this user; `null` when they signed up directly. */
  referredBy: string | null;
}

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

// `navigate` is single-user: the API rejects it with 400 if seats !== 1.
const NAVIGATE_SEATS_MESSAGE = 'Please choose 1 seat. The Navigate plan is for 1 user.';

const planFields = {
  tier: z.enum(PAID_TIERS, 'Please choose a plan.'),
  interval: z.enum(BillingInterval, 'Please choose a billing interval.'),
  seats: z
    .number('Please enter the number of seats.')
    .int()
    .min(1, 'Please choose at least 1 seat.'),
  crewSeats: z.number('Please enter the number of crew seats.').int().min(0).optional(),
  /**
   * Only a captain paying for a vessel that has no owner is asked this; the server ignores it
   * for anybody else, since the reserved seat is the owner's whatever the answer. Optional
   * because a form only carries it where the question was put — see
   * `UpdateSubscriptionDto.will_add_owner` for why it is always *sent* once it applies.
   */
  willAddOwner: z.boolean().optional(),
};

const isValidSeatsForTier = ({ tier, seats }: { tier?: SubscriptionTier; seats?: number }) =>
  tier !== SubscriptionTier.NAVIGATE || seats === undefined || seats === 1;

export const createCheckoutSubscriptionSchema = z
  .object(planFields)
  .refine(isValidSeatsForTier, { message: NAVIGATE_SEATS_MESSAGE, path: ['seats'] });

export type CreateCheckoutSubscription = z.infer<typeof createCheckoutSubscriptionSchema>;

/**
 * Every field is optional — omitted fields keep their current value, so `{ seats: 5 }`
 * changes only the seat count. Two exceptions the caller must honour:
 *  - downgrading to `navigate` requires sending `seats: 1` in the same request;
 *  - leaving `harbor` requires tier + interval + seats together (there is no existing
 *    plan to fall back on) and answers 200 + a Stripe URL instead of 204.
 */
export const updateSubscriptionSchema = z
  .object({
    tier: planFields.tier.optional(),
    interval: planFields.interval.optional(),
    seats: planFields.seats.optional(),
    crewSeats: planFields.crewSeats,
    willAddOwner: planFields.willAddOwner,
  })
  .refine(isValidSeatsForTier, { message: NAVIGATE_SEATS_MESSAGE, path: ['seats'] });

export type UpdateSubscription = z.infer<typeof updateSubscriptionSchema>;

export const cancelSubscriptionSchema = z.object({
  feedback: z.enum(CancelFeedback).optional(),
  comment: z.string().optional(),
});

export type CancelSubscription = z.infer<typeof cancelSubscriptionSchema>;
